<template>
  <div>
    <BlBackdrop :title="title" :footer="false">
      <!-- select_method -->
      <div v-if="step === 'select_method'" class="flex flex-col gap-8 pb-8">
        <div class="grid items-center grid-cols-2 gap-2 px-8">
          <div
            v-for="opt in sourceOptions"
            :key="opt.value"
            class="flex flex-col items-center px-5 py-5 text-center rounded cursor-pointer"
            :class="[
              source === opt.value
                ? 'border-2 border-light-purple bg-slate-100'
                : 'border border-slate-400 bg-pure-white m-px'
            ]"
            @click="source = opt.value"
          >
            <BlTextSubtitle3
              :text="opt.title"
              :class="{ 'text-light-purple': source === opt.value }"
            />
            <BlTextBody3 :text="opt.description" class="whitespace-pre-line w-50" />
          </div>
        </div>
        <div class="flex items-center justify-between gap-4 px-8">
          <BlInputButton
            type="plain"
            label="Cancel"
            @clicked="$emit('close-modal')"
          />
          <BlInputButton
            label="Next"
            :disabled="source === ''"
            @clicked="onSelectMethod"
          />
        </div>
      </div>

      <!-- manual_input -->
      <div v-if="step === 'manual_input'" class="flex flex-col gap-4 pb-8">
        <div class="px-8">
          <div class="flex h-10 px-1 py-1 rounded-lg bg-slate-400">
            <div
              v-for="opt in typeOptions"
              :key="opt.value"
              class="flex items-center justify-center w-full transition-all duration-300 rounded cursor-pointer"
              :class="[
                typeInput === opt.value ? 'bg-pure-white' : 'bg-slate-400'
              ]"
              @click="typeInput = opt.value"
            >
              <BlTextSubtitle3 :text="opt.label" />
            </div>
          </div>
        </div>

        <div
          class="flex flex-col gap-4"
          :class="{
            'overflow-y-auto':
              typeInput === 'CreditTransaction::Addition' ||
              (typeInput === 'CreditTransaction::Subtraction' &&
                transactionIdInput)
          }"
          :style="{ width: '40rem', maxHeight: 'calc(100vh - 16rem)' }"
        >
          <div
            v-if="typeInput === 'CreditTransaction::Addition'"
            class="flex flex-col gap-4 px-8"
          >
            <BlSelectBox
              label="Item"
              asterisk
              v-model="additionPackageItemIdInput"
              placeholder="Select item"
              :options="additionPackageItemOptions"
              @change="additionPackageItemIdInput = $event"
            />
            <div>
              <div class="flex">
                <div class="px-2 rounded-t bg-slate-400">
                  <BlTextBody3 text="Session credits" />
                </div>
              </div>
              <div
                class="flex flex-col items-center gap-1 px-4 py-3 rounded rounded-tl-none bg-slate-200"
              >
                <div class="flex items-center gap-3">
                  <BlInputButton
                    type="outlined"
                    icon="minus"
                    dense
                    class="rounded-full"
                    :disabled="additionSessionCreditInput <= 0"
                    @clicked="
                      additionSessionCreditInput =
                        Number(additionSessionCreditInput) - 1
                    "
                  />
                  <input
                    type="number"
                    name="credit_count"
                    id="credit_count"
                    :min="1"
                    v-model="additionSessionCreditInput"
                    class="flex items-center justify-center h-20 text-6xl font-bold text-center border rounded outline-none w-104 border-slate-500 text-dark-purple-100 focus:border-light-purple"
                    :class="[
                      additionSessionCreditInput <= 0
                        ? 'text-error-300'
                        : 'text-dark-purple-100'
                    ]"
                  />
                  <BlInputButton
                    type="outlined"
                    icon="plus"
                    dense
                    class="rounded-full"
                    @clicked="
                      additionSessionCreditInput =
                        Number(additionSessionCreditInput) + 1
                    "
                  />
                </div>
                <BlTextBody3 text="Credits" class="text-slate-700" />
              </div>
            </div>
          </div>

          <div
            v-if="typeInput === 'CreditTransaction::Subtraction'"
            class="flex flex-col gap-4 px-8"
          >
            <div class="grid grid-cols-2 gap-4">
              <BlSelectBox
                :class="{
                  'col-span-2':
                    !transactionInput || !transactionInput.expiration_date
                }"
                label="Source of active credit"
                asterisk
                search
                v-model="transactionIdInput"
                placeholder="Select active credits"
                :options="transactionOptions"
                @change="transactionIdInput = $event"
              />
              <BlInputDate
                v-if="transactionInput && transactionInput.expiration_date"
                :input="{
                  label: 'Expiration date',
                  model: transactionInput.expiration_date,
                  disabled: true
                }"
              />
            </div>
            <div v-if="transactionInput" class="flex flex-col gap-4">
              <div
                v-for="(service, idx) in subtractionServicesInput"
                :key="service.id"
              >
                <div class="flex">
                  <div class="px-2 rounded-t bg-slate-400">
                    <BlTextBody3 :text="service.package_item_name" />
                  </div>
                </div>
                <div
                  class="flex items-center justify-center gap-3 px-4 py-2 rounded rounded-tl-none bg-slate-200"
                >
                  <div class="flex flex-col items-center gap-1">
                    <div
                      class="flex items-center justify-center w-20 text-6xl font-bold h-18 text-slate-500"
                    >
                      {{
                        service.remaining_session_credit -
                          service.scheduled_credits.total_credits
                      }}
                    </div>
                    <BlTextBody3 text="Unused credits" />
                  </div>
                  <div class="mb-6 text-6xl font-bold text-slate-500">
                    -
                  </div>
                  <div class="flex flex-col items-center gap-1">
                    <input
                      type="number"
                      name="credit_count"
                      id="credit_count"
                      :min="0"
                      :max="
                        service.remaining_session_credit -
                          service.scheduled_credits.total_credits
                      "
                      :value="subtractionServicesInput[idx].subtract_credit"
                      @input="onInputSubtractCredits(idx, $event.target.value)"
                      class="items-center justify-center w-40 text-6xl font-bold text-center border rounded outline-none fle h-18 border-slate-500 focus:border-light-purple"
                      :class="[
                        service.remaining_session_credit -
                          service.scheduled_credits.total_credits <=
                        0
                          ? 'text-slate-500'
                          : 'text-dark-purple-100'
                      ]"
                      :disabled="
                        service.remaining_session_credit -
                          service.scheduled_credits.total_credits <=
                          0
                      "
                    />
                    <BlTextBody3 text="Subtracted amount" />
                  </div>
                  <div class="mb-6 text-6xl font-bold text-slate-500">
                    =
                  </div>
                  <div class="flex flex-col items-center gap-1">
                    <div
                      class="flex items-center justify-center w-20 text-6xl font-bold h-18 text-slate-500"
                      :class="
                        service.remaining_session_credit -
                          service.scheduled_credits.total_credits <
                        subtractionServicesInput[idx].subtract_credit
                          ? 'text-error-300'
                          : 'text-slate-500'
                      "
                    >
                      {{
                        service.remaining_session_credit -
                          service.scheduled_credits.total_credits -
                          subtractionServicesInput[idx].subtract_credit
                      }}
                    </div>
                    <BlTextBody3 text="Updated credits" />
                  </div>
                </div>
              </div>

              <div>
                <div class="flex">
                  <div class="px-2 rounded-t bg-slate-400">
                    <BlTextBody3 text="Reschedule credits" />
                  </div>
                </div>
                <div
                  class="flex items-center justify-center gap-3 px-4 py-2 rounded rounded-tl-none bg-slate-200"
                >
                  <div class="flex flex-col items-center gap-1">
                    <div
                      class="flex items-center justify-center w-20 text-6xl font-bold h-18 text-slate-500"
                    >
                      {{
                        transactionInput.remaining_reschedule_credit === null
                          ? "∞"
                          : transactionInput.remaining_reschedule_credit
                      }}
                    </div>
                    <BlTextBody3 text="Current credits" />
                  </div>
                  <div class="mb-6 text-6xl font-bold text-slate-500">
                    -
                  </div>
                  <div class="flex flex-col items-center gap-1">
                    <input
                      type="number"
                      name="credit_count"
                      id="credit_count"
                      :min="0"
                      :max="transactionInput.remaining_reschedule_credit || 0"
                      v-model="subtractionRescheduleCreditInput"
                      class="flex items-center justify-center w-40 text-6xl font-bold text-center border rounded outline-none h-18 border-slate-500 focus:border-light-purple"
                      :class="[
                        !transactionInput.remaining_reschedule_credit
                          ? 'text-slate-500'
                          : 'text-dark-purple-100'
                      ]"
                      :disabled="!transactionInput.remaining_reschedule_credit"
                    />
                    <BlTextBody3 text="Subtracted amount" />
                  </div>
                  <div class="mb-6 text-6xl font-bold text-slate-500">
                    =
                  </div>
                  <div class="flex flex-col items-center gap-1">
                    <div
                      class="flex items-center justify-center w-20 text-6xl font-bold h-18"
                      :class="
                        transactionInput.remaining_reschedule_credit !== null &&
                        transactionInput.remaining_reschedule_credit -
                          subtractionRescheduleCreditInput <
                          0
                          ? 'text-error-300'
                          : 'text-slate-500'
                      "
                    >
                      {{
                        transactionInput.remaining_reschedule_credit === null
                          ? "∞"
                          : transactionInput.remaining_reschedule_credit -
                            subtractionRescheduleCreditInput
                      }}
                    </div>
                    <BlTextBody3 text="Updated credits" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="px-8">
            <BlInputText :input="reasonInput" @changed="inputHandler" />
          </div>

          <div
            v-if="typeInput === 'CreditTransaction::Addition'"
            class="flex items-center gap-2 px-8"
          >
            <BlInputText
              :input="additionPricePerCreditInput"
              @changed="inputHandler"
            />
            <div class="pt-6">
              <BlIcon icon="arrow-right" color="slate-6" class="w-6 h-6" />
            </div>
            <BlInputText
              :input="{
                label: 'Total price',
                disabled: true,
                model: priceFormats(additionTotalPrice),
                prefix: currentCenter.currency_sym,
                prefixSpace: '3rem'
              }"
            />
          </div>

          <div
            v-if="typeInput === 'CreditTransaction::Addition'"
            class="flex flex-col gap-4 mb-4"
          >
            <div class="flex-shrink-0 w-full h-5 bg-grey-100"></div>
            <div class="flex flex-col gap-4 px-8">
              <div class="flex flex-col gap-2">
                <div
                  class="flex items-center cursor-pointer"
                  @click="setExpirationDateInput = !setExpirationDateInput"
                >
                  <div :style="{ transform: 'scale(1.375)' }" class="pl-2 pr-4">
                    <BlInputToggle
                      :checked="setExpirationDateInput"
                      @changed="
                        setExpirationDateInput = !setExpirationDateInput
                      "
                    />
                  </div>
                  <BlTextSubtitle1 text="Set expiration date" />
                </div>
                <BlTextBody2
                  text="The expiration date determines the last day credits can be used to book an appointment—unused credits will be lost after this date, but booked appointments remain valid even if they occur later."
                />
              </div>
              <div
                v-if="setExpirationDateInput"
                class="flex items-center w-2/3 gap-2"
              >
                <BlInputDate
                  :input="expirationDateInput"
                  @changed="onInputExpirationDate"
                  disabledManualInput
                />
                <div class="pt-6">
                  <BlIcon icon="horizontal-arrow" class="w-6 h-6" />
                </div>
                <div class="pt-6">
                  <BlInputText
                    :input="expirationDaysInput"
                    @changed="onInputExpirationDays"
                  />
                </div>
              </div>
            </div>
            <div class="flex-shrink-0 w-full h-5 bg-grey-100"></div>
            <div class="flex flex-col gap-4 px-8">
              <div class="flex flex-col gap-2">
                <div
                  class="flex items-center cursor-pointer"
                  @click="
                    setMaximumRescheduleInput = !setMaximumRescheduleInput
                  "
                >
                  <div :style="{ transform: 'scale(1.375)' }" class="pl-2 pr-4">
                    <BlInputToggle
                      :checked="setMaximumRescheduleInput"
                      @changed="
                        setMaximumRescheduleInput = !setMaximumRescheduleInput
                      "
                    />
                  </div>
                  <BlTextSubtitle1 text="Set maximum reschedules" />
                </div>
                <BlTextBody2
                  text="If the reschedule limit is not specified, sessions using this credit can be rescheduled infinitely."
                />
              </div>
              <div
                v-if="setMaximumRescheduleInput"
                class="grid grid-cols-2 gap-2"
              >
                <BlInputText
                  :input="maximumRescheduleInput"
                  @changed="inputHandler"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-between gap-4 px-8">
          <BlInputButton
            type="plain"
            label="Cancel"
            @clicked="$emit('close-modal')"
          />
          <div class="flex items-center gap-4">
            <BlInputButton
              v-if="enableAppointment"
              type="outlined"
              label="Back"
              @clicked="step = 'select_method'"
            />
            <BlInputButton
              label="Create"
              :disabled="isDisabledSubmit"
              :loading="submitLoading"
              @clicked="onCreate"
            />
          </div>
        </div>
      </div>

      <!-- select_package -->
      <div v-if="step === 'select_package'" class="flex flex-col gap-8 pb-8">
        <div
          class="flex flex-col gap-4 px-8"
          :style="{ width: 'calc(100vw - 16rem)' }"
        >
          <div>
            <BlInputSearch
              class="w-80"
              :input="searchInput"
              @changed="onInputSearch"
            />
          </div>

          <div v-if="fetchLoading" class="grid w-full h-64 place-items-center">
            <div class="blum-loader"></div>
          </div>
          <div
            v-else-if="!creditPackages.length"
            class="grid w-full h-64 place-items-center"
          >
            <BlTextBody2
              v-if="searchInput.model"
              text="Oops, the package(s) not found."
              class="text-center text-slate-700"
            />
            <BlTextBody2
              v-else
              text="Oops, there are no package(s) available at this center."
              class="text-center text-slate-700"
            />
          </div>
          <div
            v-else
            class="relative pb-4 overflow-auto rounded bg-slate-100 scrollbar-lg"
            :style="{ maxHeight: 'calc(100vh - 24rem)' }"
          >
            <div class="flex-shrink-0 h-4 bg-slate-100"></div>
            <div
              id="package-header"
              class="sticky top-0 z-20 flex items-center flex-grow rounded-t bg-slate-300"
            >
              <div class="sticky left-0 z-10 flex pl-6 bg-slate-100">
                <BlTooltip
                  class="flex items-center flex-shrink-0 h-12 px-4 border-t border-l border-r rounded-tl w-60 bg-slate-300 border-slate-400"
                >
                  <BlTextSubtitle3 text="Package name" class="truncate" />
                </BlTooltip>
              </div>

              <BlTooltip
                class="flex items-center flex-shrink-0 w-32 h-12 px-4 border-t bg-slate-300 border-slate-400"
              >
                <template v-slot:tooltip>
                  <div class="w-32">Number of items</div>
                </template>
                <BlTextSubtitle3 text="Number of items" class="truncate" />
              </BlTooltip>
              <BlTooltip
                class="flex items-center flex-shrink-0 w-32 h-12 px-4 border-t bg-slate-300 border-slate-400"
              >
                <BlTextSubtitle3 text="Status" class="truncate" />
              </BlTooltip>
              <BlTooltip
                class="flex items-center flex-shrink-0 w-32 h-12 px-4 border-t bg-slate-300 border-slate-400"
              >
                <BlTextSubtitle3 text="Total credits" class="truncate" />
              </BlTooltip>
              <BlTooltip
                class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
              >
                <BlTextSubtitle3 text="Total price" class="truncate" />
              </BlTooltip>
              <BlTooltip
                class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
              >
                <BlTextSubtitle3 text="Expiration date" class="truncate" />
              </BlTooltip>
              <BlTooltip
                class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
              >
                <BlTextSubtitle3 text="Max. reschedule" class="truncate" />
              </BlTooltip>
              <BlTooltip
                class="flex items-center flex-grow flex-shrink-0 h-12 px-4 border-t w-100 bg-slate-300 border-slate-400"
              >
                <BlTextSubtitle3 text="Description" class="truncate" />
              </BlTooltip>
              <div class="sticky right-0 z-10 flex pr-6 bg-slate-100">
                <div
                  class="flex items-center flex-shrink-0 w-4 h-12 border-t border-r rounded-tr bg-slate-300 border-slate-400"
                >
                  <BlTextSubtitle3 text="" />
                </div>
              </div>
            </div>

            <PackageItem
              v-for="(creditPackage, idx) in creditPackages"
              :key="creditPackage.id"
              :creditPackage="creditPackage"
              :isLast="idx + 1 === creditPackages.length"
              useSelect
              :selected="packageIdInput === creditPackage.id"
              @change-select="packageIdInput = creditPackage.id"
            />
          </div>

          <div v-if="totalCreditPackages > 25">
            <BlInputPagination
              :page="page"
              :perPage="perPage"
              :total="totalCreditPackages"
              @changed="changePage"
              @changed-per-page="changePerPage"
            />
          </div>
        </div>

        <div class="flex items-center justify-between gap-4 px-8">
          <BlInputButton
            type="plain"
            label="Cancel"
            @clicked="$emit('close-modal')"
          />
          <div class="flex items-center gap-4">
            <BlInputButton
              type="outlined"
              label="Back"
              @clicked="step = 'select_method'"
            />
            <BlInputButton
              label="Preview package"
              :disabled="isDisabledSubmit"
              @clicked="step = 'preview_package'"
            />
          </div>
        </div>
      </div>

      <!-- preview_package -->
      <div v-if="step === 'preview_package'" class="flex flex-col gap-4 pb-8">
        <div
          class="overflow-y-auto"
          :style="{ width: '40rem', maxHeight: 'calc(100vh - 14rem)' }"
        >
          <div v-if="fetchLoading" class="grid w-full h-100 place-items-center">
            <div class="blum-loader"></div>
          </div>
          <div v-else class="flex flex-col gap-4">
            <div class="flex flex-col gap-4 px-8">
              <BlInputText
                :input="{
                  label: 'Package name',
                  asterisk: true,
                  model: packageInput.name,
                  disabled: true
                }"
              />
              <BlInputText
                :input="{
                  label: 'Description',
                  textarea: true,
                  model: packageInput.description,
                  disabled: true
                }"
              />

              <div class="flex flex-col gap-2">
                <BlTextSubtitle1 text="Add package items" />
                <BlTextBody2
                  text="You can add multiple items to a package."
                  class="text-slate-700"
                />
              </div>

              <div
                class="grid grid-cols-3 gap-4 px-4 py-2 border rounded bg-slate-100 border-slate-300"
              >
                <div class="flex flex-col items-center gap-1">
                  <BlTextSubtitle1 :text="packageInput.number_of_items" />
                  <BlTextBody3 text="Total items" class="text-slate-700" />
                </div>
                <div class="flex flex-col items-center gap-1">
                  <BlTextSubtitle1 :text="packageInput.total_credits" />
                  <BlTextBody3 text="Total credits" class="text-slate-700" />
                </div>
                <div class="flex flex-col items-center gap-1">
                  <div>
                    <BlTextSubtitle1 :text="currentCenter.currency_sym" />
                    <BlTextSubtitle1
                      :text="priceFormats(packageInput.total_price)"
                    />
                  </div>
                  <BlTextBody3 text="Total price" class="text-slate-700" />
                </div>
              </div>

              <div
                v-for="item in packageInput.package_services.filter(
                  i => i.package_item.status === 'active'
                )"
                :key="item.id"
                class="flex flex-col gap-4 px-4 py-4 border rounded bg-slate-100 border-slate-300"
              >
                <div class="flex items-end gap-2">
                  <div class="flex-grow">
                    <BlSelectBox
                      label="Item"
                      asterisk
                      v-model="item.package_item.id"
                      :options="[
                        {
                          value: item.package_item.id,
                          label: item.package_item.name
                        }
                      ]"
                      disabled
                    />
                  </div>
                  <BlInputButton type="plain" icon="delete" disabled />
                </div>
                <div
                  class="flex flex-col items-center gap-1 px-4 py-3 rounded bg-slate-300"
                >
                  <div class="flex items-center gap-3">
                    <BlInputButton
                      type="outlined"
                      icon="minus"
                      dense
                      class="rounded-full"
                      disabled
                    />
                    <input
                      type="number"
                      :name="`credit_count_${item.id}`"
                      :id="`credit_count_${item.id}`"
                      :value="item.session_credit"
                      class="flex items-center justify-center h-20 text-6xl font-bold text-center border rounded outline-none w-104 border-slate-500 text-dark-purple-100 focus:border-light-purple"
                      disabled
                    />
                    <BlInputButton
                      type="outlined"
                      icon="plus"
                      dense
                      class="rounded-full"
                      disabled
                    />
                  </div>
                  <BlTextBody3 text="Credits" class="text-slate-700" />
                </div>
                <div class="flex items-center gap-2">
                  <BlInputText
                    :input="{
                      label: 'Standard retail price',
                      model: priceFormats(item.standard_retail_price),
                      prefix: currentCenter.currency_sym,
                      prefixSpace: '3rem',
                      disabled: true
                    }"
                  />
                  <div class="pt-6">
                    <BlIcon
                      icon="horizontal-arrow"
                      color="slate-6"
                      class="w-6 h-6"
                    />
                  </div>
                  <BlInputText
                    :input="{
                      label: 'Total price',
                      model: priceFormats(item.total_price),
                      prefix: currentCenter.currency_sym,
                      prefixSpace: '3rem',
                      disabled: true
                    }"
                  />
                </div>
              </div>
            </div>
            <div class="flex-shrink-0 w-full h-5 bg-grey-100"></div>
            <div class="flex flex-col gap-4 px-8">
              <div class="flex flex-col gap-2">
                <div class="flex items-center">
                  <div :style="{ transform: 'scale(1.375)' }" class="pl-2 pr-4">
                    <BlInputToggle
                      :checked="packageInput.expiration_date !== null"
                      disabled
                    />
                  </div>
                  <BlTextSubtitle1 text="Set expiration date" />
                </div>
                <BlTextBody2
                  text="The expiration date determines the last day credits can be used to book an appointment—unused credits will be lost after this date, but booked appointments remain valid even if they occur later."
                />
              </div>
              <div
                v-if="packageInput.expiration_date !== null"
                class="flex items-center w-2/3 gap-2"
              >
                <BlInputDate
                  :input="{
                    label: 'Expiration date',
                    asterisk: true,
                    model: getDateFromToday(packageInput.expiration_date),
                    disabled: true
                  }"
                />
                <div class="pt-6">
                  <BlIcon icon="horizontal-arrow" class="w-6 h-6" />
                </div>
                <div class="pt-6">
                  <BlInputText
                    :input="{
                      model: packageInput.expiration_date,
                      inputType: 'number',
                      prefix: 'in',
                      prefixSpace: '2.5rem',
                      suffix: 'days',
                      disabled: true
                    }"
                  />
                </div>
              </div>
            </div>
            <div class="flex-shrink-0 w-full h-5 bg-grey-100"></div>
            <div class="flex flex-col gap-4 px-8">
              <div class="flex flex-col gap-2">
                <div class="flex items-center">
                  <div :style="{ transform: 'scale(1.375)' }" class="pl-2 pr-4">
                    <BlInputToggle
                      :checked="packageInput.reschedule_credit !== null"
                      disabled
                    />
                  </div>
                  <BlTextSubtitle1 text="Set maximum reschedules" />
                </div>
                <BlTextBody2
                  text="If the reschedule limit is not specified, sessions using this credit can be rescheduled infinitely."
                />
              </div>
              <div
                v-if="packageInput.reschedule_credit !== null"
                class="grid grid-cols-2 gap-2"
              >
                <BlInputText
                  :input="{
                    label: 'Maximum reschedule allowed',
                    model: packageInput.reschedule_credit,
                    inputType: 'number',
                    suffix: 'time(s)',
                    disabled: true
                  }"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-between gap-4 px-8">
          <BlInputButton
            type="plain"
            label="Cancel"
            @clicked="$emit('close-modal')"
          />
          <div class="flex items-center gap-4">
            <BlInputButton
              type="outlined"
              label="Back"
              @clicked="step = 'select_package'"
            />
            <BlInputButton
              label="Use package"
              :disabled="isDisabledSubmit"
              :loading="submitLoading"
              @clicked="onCreateWithPackage"
            />
          </div>
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="creditMinusHandlingOpen"
      title="Credit balance adjustment"
      :footer="false"
    >
      <div class="mb-8">
        <div class="flex flex-col gap-4 px-8 mb-4">
          <div>
            <BlTextBody2 text="Your current credit balance is " />
            <BlTextBody2 :text="`${availableCredits}. `" class="font-bold" />
            <BlTextBody2 text="Adding " />
            <BlTextBody2 :text="`${addingCredits} `" class="font-bold" />
            <BlTextBody2 text="new credits will adjust your " />
            <br />
            <BlTextBody2 text="balance to a final total of: " />
          </div>
          <div
            class="flex flex-col items-center justify-center gap-1 py-3 rounded bg-slate-200"
          >
            <div
              class="flex items-center text-6xl font-bold h-18 text-dark-purple-100"
            >
              {{ finalAvailableCredits }}
            </div>
            <BlTextBody3 text="Final available credit(s)" />
          </div>
        </div>
        <div class="flex items-center justify-end gap-4 px-8 pt-4">
          <BlInputButton
            type="plain"
            label="Cancel"
            @clicked="creditMinusHandlingOpen = false"
          />
          <BlInputButton
            v-if="source === 'manual'"
            label="Confirm and create"
            :disabled="isDisabledSubmit"
            :loading="submitLoading"
            @clicked="onCreate"
          />
          <BlInputButton
            v-if="source === 'package'"
            class="max-w-64"
            :label="`Confirm and use ${packageInput.name}`"
            :disabled="isDisabledSubmit"
            :loading="submitLoading"
            @clicked="onCreateWithPackage"
          />
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import debounce from "lodash/debounce";
import { displayPrice } from "@/plugins/helper.js";

import PackageItem from "../../center/package/Item.vue";

import BlBackdrop from "../../BlBackdrop.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlTooltip from "../../BlTooltip.vue";
import BlInputDate from "../../BlInputDate.vue";
import BlInputText from "../../BlInputText.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputToggle from "../../BlInputToggle.vue";
import BlInputSearch from "../../BlInputSearch.vue";
import BlInputPagination from "../../BlInputPagination.vue";

export default {
  name: "NewCreditTransactionModal",
  components: {
    PackageItem,
    BlBackdrop,
    BlTextSubtitle1,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlTextBody3,
    BlTooltip,
    BlInputDate,
    BlInputText,
    BlSelectBox,
    BlInputButton,
    BlIcon,
    BlInputToggle,
    BlInputSearch,
    BlInputPagination
  },
  data() {
    return {
      submitLoading: false,
      step: "select_method", // select_method, manual_input, select_package, preview_package
      source: "", // manual | package
      sourceOptions: [
        {
          value: "manual",
          title: "Create new credit transaction",
          description: "Customize credits tailored to the client."
        },
        {
          value: "package",
          title: "Select from Packages",
          description: "Select credits that have been set by the center."
        }
      ],
      typeInput: "CreditTransaction::Addition", // CreditTransaction::Addition | CreditTransaction::Subtraction
      typeOptions: [
        { value: "CreditTransaction::Addition", label: "Addition" },
        { value: "CreditTransaction::Subtraction", label: "Subtraction" }
      ],
      issueDateInput: {
        label: "Issued date",
        model: moment().format("YYYY-MM-DD")
      },
      reasonInput: {
        label: "Reason",
        asterisk: true,
        placeholder: "Type the reason",
        textarea: true,
        model: ""
      },
      // CreditTransaction::Addition
      additionPackageItemIdInput: 0,
      additionPackageItemOptions: [],
      additionSessionCreditInput: 0,
      additionPricePerCreditInput: {
        label: "Price per credit",
        model: "0",
        disabled: false,
        prefix: "",
        prefixSpace: "3rem"
      },
      setExpirationDateInput: false,
      expirationDateInput: {
        label: "Expiration date",
        asterisk: true,
        model: this.getDateFromToday(30),
        min: moment().format("YYYY-MM-DD")
      },
      expirationDaysInput: {
        model: "30",
        min: 0,
        inputType: "number",
        prefix: "in",
        prefixSpace: "3rem",
        suffix: "days"
      },
      setMaximumRescheduleInput: false,
      maximumRescheduleInput: {
        label: "Maximum reschedule allowed",
        model: "3",
        min: 1,
        inputType: "number",
        suffix: "time(s)"
      },
      // CreditTransaction::Subtraction
      transactionIdInput: 0,
      transactionInput: null,
      transactionOptions: [],
      subtractionServicesInput: [],
      subtractionRescheduleCreditInput: 0,
      //
      fetchLoading: false,
      packageIdInput: 0,
      packageInput: null,
      page: 1,
      perPage: 25,
      searchInput: { placeholder: "Search package by name", model: "" },
      //
      creditMinusHandlingOpen: false,
      addingCredits: 0,
      finalAvailableCredits: 0
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "client",
      "creditPackages",
      "totalCreditPackages",
      "creditTransactionDetails"
    ]),
    enableAppointment() {
      return this.currentCenter.enable_appointment;
    },
    title() {
      const titles = {
        select_method: "Select source of credit",
        manual_input: "New credit transaction",
        select_package: "Select from Packages",
        preview_package: "Preview package"
      };
      return titles[this.step] || "New credit transaction";
    },
    isDisabledSubmit() {
      if (this.source === "manual") {
        if (!this.reasonInput.model) return true;
        if (this.typeInput === "CreditTransaction::Addition") {
          if (!this.additionPackageItemIdInput) return true;
          if (this.additionSessionCreditInput <= 0) return true;
        }
        if (this.typeInput === "CreditTransaction::Subtraction") {
          if (!this.transactionIdInput) return true;
          if (!this.transactionInput) return true;

          const reschedule = this.transactionInput.remaining_reschedule_credit;
          if (reschedule !== null) {
            if (reschedule < this.subtractionRescheduleCreditInput) return true;
          }

          let exceedLimit = false;
          this.subtractionServicesInput.forEach(i => {
            if (i.remaining_session_credit < i.subtract_credit) {
              exceedLimit = true;
            }
          });
          if (exceedLimit) return true;
        }
      }
      if (this.source === "package") {
        if (!this.packageIdInput) return true;
      }
      return false;
    },
    //
    additionTotalPrice() {
      const count = Number(this.additionSessionCreditInput);
      const price = Number(this.additionPricePerCreditInput.model);
      return count * price;
    },
    //
    availableCredits() {
      if (!this.creditTransactionDetails) return 0;
      if (!this.creditTransactionDetails.credit_detail) return 0;
      if (!this.creditTransactionDetails.credit_detail.session_credits) {
        return 0;
      }
      return (
        this.creditTransactionDetails.credit_detail.session_credits.available ||
        0
      );
    }
  },
  watch: {
    async step(val) {
      if (val === "manual_input") {
        this.fetchCreditPackageItems();
        this.fetchTransactions();
      }
      if (val === "select_package") {
        this.page = 1;
        this.perPage = 25;
        this.fetchPackages();
      }
      if (val === "preview_package") {
        const params = { id: this.packageIdInput };
        this.fetchLoading = true;
        const { success, data } = await this.getCreditPackage(params);
        this.fetchLoading = false;
        if (!success) return;
        this.packageInput = data;
      }
    },
    typeInput(val) {
      if (val === "CreditTransaction::Addition") {
      }
      if (val === "CreditTransaction::Subtraction") {
      }
    },
    additionPackageItemIdInput(val) {
      this.additionPricePerCreditInput.model = 0;
      if (!val) return;

      const item = this.additionPackageItemOptions.find(i => i.value === val);
      this.additionPricePerCreditInput.model = item.standard_retail_price;
    },
    async transactionIdInput(val) {
      this.transactionInput = null;
      if (!val) return;

      const params = { client_id: this.client.id, id: val };
      const { success, data } = await this.getCreditTransaction(params);
      if (!success) return;
      this.transactionInput = data;
      this.subtractionServicesInput = data.available_services.map(i => {
        return { ...i, subtract_credit: 0 };
      });
    }
  },
  mounted() {
    if (this.enableAppointment) {
      this.step = "select_method";
      this.source = "";
    } else {
      this.step = "manual_input";
      this.source = "manual";
    }

    if (this.currentCenter) {
      this.additionPricePerCreditInput.prefix = this.currentCenter.currency_sym;
    }
  },
  methods: {
    ...mapActions([
      "getCreditPackageItems",
      "getCreditTransaction",
      "getCreditTransactions",
      "createCreditTransaction",
      //
      "getCreditPackage",
      "getCreditPackages",
      "createCreditTransactionWithPackage"
    ]),
    displayPrice,
    priceFormats(price = 0) {
      const settings = this.currentCenter.currency_settings;
      return this.displayPrice({ settings, price });
    },
    onSelectMethod() {
      const steps = {
        manual: "manual_input",
        package: "select_package"
      };
      this.step = steps[this.source];
    },
    //
    async fetchCreditPackageItems() {
      this.additionPackageItemIdInput = 0;
      this.additionPackageItemOptions = [];

      let params = `?page=1&per_page=100&client_id=${this.client.id}`;
      params += `&sort=name_asc`;
      params += `&status=active`;

      const { success, data } = await this.getCreditPackageItems({ params });
      if (!success) return;
      if (!data.package_items.length) return;
      this.additionPackageItemOptions = data.package_items.map(i => {
        return { value: i.id, label: i.name, ...i };
      });

      const isDefault = data.package_items.find(i => i.is_default);
      const item = isDefault || data.package_items[0];
      this.additionPackageItemIdInput = item.id;
    },
    async fetchTransactions() {
      this.transactionIdInput = 0;
      this.transactionOptions = [];

      const params = {
        client_id: this.client.id,
        search: true,
        params: `?per_page=50&sort=newest&type=CreditTransaction::Addition&status=available`
      };

      const { success, data } = await this.getCreditTransactions(params);
      if (!success) return;
      if (!data.credit_transactions.length) return;
      this.transactionOptions = data.credit_transactions.map(i => {
        return { value: i.id, label: `${i.id} ${i.accountable_string}`, ...i };
      });
    },
    //
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    onInputExpirationDate(payload) {
      payload.input.model = payload.value;
      let val = payload.value;
      if (val) {
        const today = moment().startOf("day");
        val = moment(val, "YYYY-MM-DD");
        const diff = val.diff(today, "days");
        this.expirationDaysInput.model = diff;
      }
    },
    onInputExpirationDays(payload) {
      payload.input.model = payload.value;
      let val = payload.value;
      if (val) {
        const today = moment().startOf("day");
        val = Number(val);
        const date = today.add(val, "days").format("YYYY-MM-DD");
        this.expirationDateInput.model = date;
      }
    },
    onInputSubtractCredits(idx, value) {
      const val = Number(value);
      this.subtractionServicesInput[idx].subtract_credit = val;
    },
    //
    onCreate() {
      if (this.availableCredits < 0 && !this.creditMinusHandlingOpen) {
        const credits = Number(this.additionSessionCreditInput);
        this.addingCredits = credits;
        this.finalAvailableCredits = credits + Number(this.availableCredits);
        this.creditMinusHandlingOpen = true;
      } else {
        this.onProcessCreate();
      }
    },
    async onProcessCreate() {
      const add = {
        type: this.typeInput,
        issued_date: this.issueDateInput.model,
        reason: this.reasonInput.model,
        expiration_date: null,
        reschedule_credit: null,
        credit_transaction_services_attributes: [
          {
            package_item_id: this.additionPackageItemIdInput,
            session_credit: this.additionSessionCreditInput,
            total_price: this.additionTotalPrice
          }
        ]
      };
      if (this.setExpirationDateInput) {
        add.expiration_date = this.expirationDateInput.model;
      }
      if (this.setMaximumRescheduleInput) {
        add.reschedule_credit = this.maximumRescheduleInput.model;
      }

      const subtract = {
        type: this.typeInput,
        issued_date: this.issueDateInput.model,
        reason: this.reasonInput.model,
        transaction_addition_id: this.transactionIdInput,
        reschedule_credit: this.subtractionRescheduleCreditInput || null,
        credit_transaction_services_attributes: this.subtractionServicesInput
          .filter(i => i.subtract_credit > 0)
          .map(i => ({
            package_item_id: i.package_item_id,
            session_credit: i.subtract_credit,
            transaction_service_addition_id: i.id
          }))
      };

      const params = {
        client_id: this.client.id,
        credit_transaction: this.typeInput.includes("Addition") ? add : subtract
      };

      this.submitLoading = true;
      const { success } = await this.createCreditTransaction(params);
      this.submitLoading = false;
      if (!success) return;

      if (this.typeInput.includes("Subtraction")) {
        this.$emit("after-commit");
      }

      this.$emit("close-modal");
    },
    //
    async fetchPackages() {
      let params = `?page=${this.page}&per_page=${this.perPage}`;
      const query = this.searchInput.model;
      if (query) params += `&name=${query}`;
      params += `&status=active`;
      params += `&sort=name_asc`;

      this.fetchLoading = true;
      await this.getCreditPackages({ params });
      this.fetchLoading = false;
    },
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchPackages();
    }, 1000),
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchPackages();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchPackages();
    },
    getDateFromToday(num = 0) {
      return moment()
        .add(num, "days")
        .format("YYYY-MM-DD");
    },
    onCreateWithPackage() {
      if (this.availableCredits < 0 && !this.creditMinusHandlingOpen) {
        const credits = Number(this.packageInput.session_credit);
        this.addingCredits = credits;
        this.finalAvailableCredits = credits + Number(this.availableCredits);
        this.creditMinusHandlingOpen = true;
      } else {
        this.onProcessCreateWithPackage();
      }
    },
    async onProcessCreateWithPackage() {
      const data = {
        client_id: this.client.id,
        package_id: this.packageIdInput,
        package_name: this.packageInput.name,
        package_credit: this.packageInput.session_credit
      };
      this.submitLoading = true;
      const { success } = await this.createCreditTransactionWithPackage(data);
      this.submitLoading = false;
      if (!success) return;
      this.$emit("close-modal");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/creditTransaction/NewCreditTransactionModal.vue